import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import { getUserNotifications as getUserNotificationsApi } from "@/api/user";
import {
  deleteNotificationById as deleteNotificationByIdApi,
  readNotifications as readNotificationsApi,
  acceptOfferNotifications as acceptOfferNotificationsApi,
  declineOfferNotifications as declineOfferNotificationsApi,
  acceptInterviewNotifications as acceptInterviewNotificationsApi,
  declineInterviewNotifications as declineInterviewNotificationsApi,
} from "@/api/misc";
import { getOrganizationNotifications as getOrganizationNotificationsApi } from "@/api/organization";
import { declineApplyRequest } from "@/api/job";
import { acceptReportRequestById, declineReportRequestById } from "@/api/backgroundCheck";

const NOTIFICATION_TYPES = {
  job_applied: "job_applied",
  role_accepted: "role_accepted",
  role_declined: "role_declined",
  applicant_found: "applicant_found",
  interview_accepted: "interview_accepted",
  interview_declined: "interview_declined",
  interview_reminder: "interview_reminder",
  interview_updated_employer: "interview_updated_employer",
  interview_updated_jobseeker: "interview_updated_jobseeker",
  apply_request: "apply_request",
  shortlisted: "shortlisted",
  interview_request: "interview_request",
  profile_views: "profile_views",
  new_post: "new_post",
  new_jobs: "new_jobs",
  role_request: "role_request",
  chat: "chat",
};

interface InterviewRequestData {
  organization_id: string;
  meeting_id: string;
  organization_logo: string;
  organization_name: string;
  job_title: string;
  mode: string;
  start_time: string;
  start_time_ts: number;
  timezone: string;
  duration: number;
  meeting: {
    id: string;
    status: string;
  };
}

interface ShortlistedData {
  job_id: string;
  application_id: string;
  organization_logo: string;
  organization_name: string;
  job_title: string;
  match_percentage: number;
  match_attr: string[];
  job: {
    applied_count: number;
  };
}

type NotificationData = ShortlistedData | InterviewRequestData;

interface Notification {
  id: string;
  message: string;
  read: boolean;
  type: keyof typeof NOTIFICATION_TYPES;
  data: any;
  created_at: string;
  // TODO: Craft the notifications data based on the type of notifications
}

interface NotificationState {
  data: {
    next_cursor: string | null;
    notifications: Notification[];
    unread_count: number;
  };
  isFetchingData: boolean;
}

const initialState: NotificationState = {
  data: {
    next_cursor: null,
    notifications: [],
    unread_count: 0,
  },
  isFetchingData: false,
};

// Async thunks
export const getUserNotifications = createAsyncThunk(
  "notification/getUserNotifications",
  async ({ userId, cursor }: { userId?: string; cursor?: string }) => {
    const notifications = await getUserNotificationsApi({ userId }, { cursor });
    return notifications;
  },
);

export const getOrganizationNotifications = createAsyncThunk(
  "notification/getOrganizationNotifications",
  async ({ organizationId, cursor }: { organizationId: string; cursor?: string }) => {
    const notifications = await getOrganizationNotificationsApi({ organizationId }, { cursor });
    return notifications;
  },
);

export const readNotifications = createAsyncThunk(
  "notification/readNotifications",
  async ({ userId = null }: { userId?: string }) => {
    const notifications = await readNotificationsApi(userId);
    return notifications;
  },
);

export const declineJobRequestNotificationsById = createAsyncThunk(
  "notification/declineJobRequestNotificationsById",
  async ({
    jobId = null,
    applyRequestId = null,
    notificationId,
  }: {
    jobId?: string;
    applyRequestId?: string;
    notificationId: string;
  }) => {
    await declineApplyRequest(jobId, applyRequestId);
    return notificationId;
  },
);

export const acceptOfferNotificationsById = createAsyncThunk(
  "notification/acceptOfferNotificationsById",
  async ({
    jobId = null,
    offerId = null,
    notificationId,
  }: {
    jobId?: string;
    offerId?: string;
    notificationId: string;
  }) => {
    await acceptOfferNotificationsApi(jobId, offerId);
    return notificationId;
  },
);

export const declineOfferNotificationsById = createAsyncThunk(
  "notification/declineOfferNotificationsById",
  async ({
    jobId = null,
    offerId = null,
    notificationId,
  }: {
    jobId?: string;
    offerId?: string;
    notificationId: string;
  }) => {
    await declineOfferNotificationsApi(jobId, offerId);
    return notificationId;
  },
);

export const acceptInterviewNotificationsById = createAsyncThunk(
  "notification/acceptInterviewNotificationsById",
  async ({
    organizationId = null,
    meetingId = null,
    notificationId,
  }: {
    organizationId?: string;
    meetingId?: string;
    notificationId: string;
  }) => {
    await acceptInterviewNotificationsApi(organizationId, meetingId);
    return notificationId;
  },
);

export const declineInterviewNotificationsById = createAsyncThunk(
  "notification/declineInterviewNotificationsById",
  async ({
    organizationId = null,
    meetingId = null,
    notificationId,
  }: {
    organizationId?: string;
    meetingId?: string;
    notificationId: string;
  }) => {
    await declineInterviewNotificationsApi(organizationId, meetingId);
    return notificationId;
  },
);

export const shareReportRequest = createAsyncThunk(
  "notification/shareReportRequest",
  async (notificationId: string, { rejectWithValue }) => {
    try {
      await acceptReportRequestById(notificationId);
      return notificationId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const declineReportRequest = createAsyncThunk(
  "notification/declineReportRequest",
  async (notificationId: string, { rejectWithValue }) => {
    try {
      await declineReportRequestById(notificationId);
      return notificationId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);


export const deleteNotificationById = createAsyncThunk(
  "notification/deleteNotificationById",
  async ({
    userId = null,
    organizationId = null,
    notificationId,
  }: {
    userId?: string;
    organizationId?: string;
    notificationId: string;
  }) => {
    await deleteNotificationByIdApi(organizationId, userId, notificationId);
    return notificationId;
  },
);

export const clearNotifications = createAsyncThunk(
  "notification/clearNotifications",
  async (_, { dispatch }) => {
    dispatch(notificationSlice.actions.resetNotifications());
  },
);

const notificationSlice = createSlice({
  name: "notification",
  initialState,
  reducers: {
    resetNotifications(state) {
      state.data = initialState.data;
      state.isFetchingData = initialState.isFetchingData;
    },
    // sortNotifications(state) {
    //   state.data.notifications.sort((a, b) => {
    //     return new Date(b.data.created_at).getTime() - new Date(a.data.created_at).getTime();
    //   });
    // },
  },
  extraReducers: (builder) => {
    builder
      .addCase(deleteNotificationById.fulfilled, (state, action: PayloadAction<string>) => {
        const deletedNotificationId = action.payload;
        state.data.notifications = state.data.notifications.filter(
          (notification) => notification.id !== deletedNotificationId,
        );
      })
      .addCase(
        declineJobRequestNotificationsById.fulfilled,
        (state, action: PayloadAction<string>) => {
          const notificationId = action.payload;
          const notification = state.data.notifications.find(
            (notification) => notification.id === notificationId,
          );
          if (notification && notification.data) {
            notification.data.apply_request.status = "declined";
          }
        },
      )
      .addCase(
        acceptInterviewNotificationsById.fulfilled,
        (state, action: PayloadAction<string>) => {
          const notificationId = action.payload;
          const notification = state.data.notifications.find(
            (notification) => notification.id === notificationId,
          );
          if (notification && notification.data) {
            notification.data.meeting.status = "accepted";
          }
        },
      )
      .addCase(
        declineInterviewNotificationsById.fulfilled,
        (state, action: PayloadAction<string>) => {
          const notificationId = action.payload;
          const notification = state.data.notifications.find(
            (notification) => notification.id === notificationId,
          );
          if (notification && notification.data) {
            notification.data.meeting.status = "rejected";
          }
        },
      )
      .addCase(acceptOfferNotificationsById.fulfilled, (state, action: PayloadAction<string>) => {
        const notificationId = action.payload;
        const notification = state.data.notifications.find(
          (notification) => notification.id === notificationId,
        );
        if (notification && notification.data) {
          notification.data.offer.status = "accepted";
        }
      })
      .addCase(declineOfferNotificationsById.fulfilled, (state, action: PayloadAction<string>) => {
        const notificationId = action.payload;
        const notification = state.data.notifications.find(
          (notification) => notification.id === notificationId,
        );
        if (notification && notification.data) {
          notification.data.offer.status = "rejected";
        }
      })
      .addCase(getUserNotifications.pending, (state) => {
        state.isFetchingData = true;
      })
      .addCase(getUserNotifications.fulfilled, (state, action: PayloadAction<any>) => {
        state.data = {
          ...action.payload,
          notifications: [
            ...state.data.notifications.filter(
              (notification) =>
                !action.payload.notifications.some(
                  (newNotification) => newNotification.id === notification.id,
                ),
            ),
            ...action.payload.notifications,
          ],
        };
        state.isFetchingData = false;
        // notificationSlice.caseReducers.sortNotifications(state);
      })
      .addCase(getOrganizationNotifications.pending, (state) => {
        state.isFetchingData = true;
      })
      .addCase(getOrganizationNotifications.fulfilled, (state, action: PayloadAction<any>) => {
        state.data = {
          ...action.payload,
          notifications: [
            ...state.data.notifications.filter(
              (notification) =>
                !action.payload.notifications.some(
                  (newNotification) => newNotification.id === notification.id,
                ),
            ),
            ...action.payload.notifications,
          ],
        };
        state.isFetchingData = false;
      })
      .addCase(getUserNotifications.rejected, (state) => {
        state.isFetchingData = false;
      })
      .addCase(shareReportRequest.fulfilled, (state, action: PayloadAction<string>) => {
        const notificationId = action.payload;
        const notification = state.data.notifications.find(
          (notification) => notification?.data?.bc_report_request?.id === notificationId
        );
        if (notification && notification.data) {
          notification.data.bc_report_request.status = "shared";
          state.data.notifications = [...state.data.notifications];
          
        }
      })
      .addCase(shareReportRequest.rejected, (state, action) => {
        console.error("Failed to share report request:", action.payload);
      })
      .addCase(declineReportRequest.fulfilled, (state, action: PayloadAction<string>) => {
        const notificationId = action.payload;
        const notification = state.data.notifications.find(
          (notification) => notification?.data?.bc_report_request?.id === notificationId
        );
        if (notification && notification.data) {
          notification.data.bc_report_request.status = "declined";
          state.data.notifications = [...state.data.notifications]; 
        }
      })
      .addCase(declineReportRequest.rejected, (state, action) => {
        console.error("Failed to decline report request:", action.payload);
      });
  },
});

export default notificationSlice.reducer;
